export default function JacksonvilleTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Jacksonville Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Jacksonville travelers actually move helps us anticipate the passport urgencies that arise here. From military PCS timelines to port cruise departures, Northeast Florida travel operates on a different rhythm than most U.S. markets.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* JAX Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">JAX — Jacksonville International Airport</h3>
                <p className="text-gray-400 text-xs">Northside Jacksonville, 15 miles from downtown</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              JAX is the primary commercial airport for Northeast Florida travelers. It offers nonstop service to major international hubs including Atlanta, Charlotte, Miami, New York, and Toronto — with many Jacksonville residents connecting to Europe, Latin America, and Asia. Military personnel and their families frequently fly through JAX for PCS moves and deployment rotations, often with passport validity requirements that standard processing cannot meet.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking JAX international connections. Many countries require six months validity beyond your return date — and military travel orders often have non-negotiable departure windows.
              </p>
            </div>
          </div>

          {/* JAXPORT & Cruises */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-ship-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">JAXPORT &amp; Cruise Departures</h3>
                <p className="text-gray-400 text-xs">St. Johns River cruise terminal</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              JAXPORT is one of Florida's busiest vehicle export terminals and serves seasonal cruise departures to the Bahamas and Caribbean. Jacksonville-area families, retirees along the St. Johns River, and military families book cruise vacations with relatively short lead times — then discover a child's passport expired or a dependent never had one. Repositioning cruises and last-minute cruise deals create compressed booking-to-sailing timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Even Bahamas cruises may require passports for all passengers. Some cruise lines accept birth certificates for closed-loop sailings, but any international flight connection or non-U.S. port requires a passport. Verify before booking.
              </p>
            </div>
          </div>

          {/* Military PCS & Deployment */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shield-star-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Military PCS &amp; Deployment Cycles</h3>
                <p className="text-gray-400 text-xs">NAS Jacksonville, Mayport, Blount Island</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Jacksonville's three major military installations create one of the most time-sensitive passport markets in Florida. PCS orders often arrive with 30 days or less to report. Dependent passports must be current for overseas assignments. Deployment leave windows are fixed and unmovable. We regularly process expedited passports for Navy and Marine families who received orders with short-fuse timelines — including infant passports for newborns before an overseas PCS.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Dependent passports must have at least six months validity for most overseas PCS assignments. Check every family member's expiration as soon as orders arrive — not the week before your flight to JAX.
              </p>
            </div>
          </div>

          {/* College & Study Abroad */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">College &amp; Study Abroad Seasons</h3>
                <p className="text-gray-400 text-xs">UNF, JU, FSCJ, nearby Flagler College</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Jacksonville-area universities send hundreds of students abroad each year — summer programs in Spain, semester exchanges in Germany, research trips to Costa Rica. Application deadlines for study-abroad programs often fall in October and February, but many students do not think about their passport until after acceptance. Standard post office processing frequently exceeds program deadlines, making expedited service essential for students who need their documents before orientation.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants (many college students) require an in-person acceptance clerk visit. We guide UNF and JU students through the exact location, forms, and timeline so they do not miss their program orientation.
              </p>
            </div>
          </div>
        </div>

        {/* Mayo Clinic / Medical Travel callout */}
        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-heart-pulse-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Mayo Clinic Jacksonville &amp; Medical Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Mayo Clinic Jacksonville draws international patients and medical tourists from across the Southeast. Many families traveling for treatment, second opinions, or clinical trials discover passport issues while booking travel. We accommodate urgent medical travel timelines with same-day document review and expedited submission when every day matters.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}